import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import React, { useEffect, Suspense } from 'react';
import {
  QTY_SELECTION_STEP,
  REFUND_METHOD_STEP,
  RETURN_METHOD_STEP,
  SELECT_ITEMS_TO_RETURN_STEP,
  NUMBER_OF_BOXES_STEP,
  FINALIZE_RETURN_STEP,
  LORP_WHAT_TO_EXPECT_STEP,
  LORP_RETURN_METHOD_STEP,
} from '@src/utils/returnFlow';
import { buildReturnEligibilityRequest } from '@src/utils';
import { IReturnEligibilityResponse } from '@src/models/api';
import useFetchState from '@src/hooks/useFetchState';
import { useNewFlowStore } from '@src/stores/newFlowStore';
import { useIsInStoreOnlyReturn } from '@src/hooks/returns-hooks';
import ItemsToReturnSkeleton from './select-items/ItemsToReturnSkeleton';
import {
  getReturnEligibilityDetails,
  useReturnStore,
} from '../stores/returnStore';
import SelectItemsToReturn from './select-items/SelectItemsToReturn';
import { ReturnMethodSkeletonLoader } from './return-method/ReturnMethodHelper';
import { ReturnButtons } from './shared/ReturnButtons';
import QuantitySelectionStep from './quantity-selection/QuantitySelectionStep';
import SelectReturnMethodStep from './return-method/SelectReturnMethodStep';
import RefundMethodStep from './refund-method/RefundMethodStep';
import ReviewReturnDetailsStep from './review-return-details/ReviewReturnDetailsStep';
import { DropOffBoxes } from './boxes-dropoff/DropOffBoxes';
import LORPWhatToExpect from './lorp/lorp-what-to-expect/LORPWhatToExpect';
import LORPReturnMethodFlow from './lorp/LORPReturnMethodFlow';
import { Phase0InStoreReturnMethod } from './return-method/phase-0-instore/Phase0InStoreReturnMethod';
import ErrorBanner from './shared/ErrorBanner';

export const ReturnForm = () => {
  const currentStep = useNewFlowStore((state) => state.currentStep);
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const militaryExchange = useReturnStore((state) => state.militaryExchange);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const isCashNCarryOrderNumber = orderDetailsV2?.isCashNCarryOrderNumber;
  const isInStoreOnlyReturn = useIsInStoreOnlyReturn();

  // Zustand state — persists across mount/unmount cycles. Used as the source
  // of truth so that if a previous mount already started or completed the
  // eligibility fetch, this instance picks up where it left off.
  const returnEligibilityLoading = useReturnStore(
    (state) => state.returnEligibilityLoading,
  );
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );

  const { loading, error, fetchData, isFetched } =
    useFetchState<IReturnEligibilityResponse>();

  // Combine local + global state: consider "fetched" if either the local
  // useFetchState completed OR the Zustand store already has the data.
  // Consider "loading" if either source says so.
  const isEligibilityFetched = isFetched || !!returnEligibilityDetails;
  const isEligibilityLoading =
    loading || (returnEligibilityLoading && !isEligibilityFetched);

  const callCheckEligibility = () => {
    if (!orderDetailsV2) return;
    const items = orderDetailsV2?.lineItems || [];
    const requestData = buildReturnEligibilityRequest(
      orderDetailsV2,
      isMilitaryExchangeGuest,
      items,
      true,
    );
    fetchData(() => getReturnEligibilityDetails(requestData));
  };

  useEffect(() => {
    // Check Zustand store directly — if a previous mount already started or
    // completed the eligibility fetch, skip it. This prevents the double-call
    // that happens when the component tree unmounts/remounts during hydration.
    const {
      returnEligibilityDetails: existingDetails,
      returnEligibilityLoading: existingLoading,
    } = useReturnStore.getState();
    if (existingDetails || existingLoading) return;
    if (orderDetailsV2 && !isCashNCarryOrderNumber) {
      callCheckEligibility();
    }
  }, [orderDetailsV2, militaryExchange]);

  // Cash and Carry orders will not go through eligibility check and will instead display static page with barcode
  // Similarly, orders that are only eligible for in-store returns via OMS and checkEligibility will also see the Phase 0 page
  if (isCashNCarryOrderNumber || isInStoreOnlyReturn) {
    return <Phase0InStoreReturnMethod />;
  }
  if (!isEligibilityFetched || isEligibilityLoading) {
    return <ReturnMethodSkeletonLoader />;
  }
  if (error) {
    return (
      <>
        <ErrorBanner />
        <ReturnButtons
          isError
          proceedNext={() => callCheckEligibility()}
          disabled={!isEligibilityFetched || isEligibilityLoading}
        />
      </>
    );
  }

  return (
    <div>
      <ErrorBoundary name="return-form">
        {currentStep === SELECT_ITEMS_TO_RETURN_STEP && (
          <div className="sui-mb-4">
            <ErrorBoundary name="select-items-to-return-step">
              <Suspense fallback={<ItemsToReturnSkeleton />}>
                <SelectItemsToReturn />
              </Suspense>
            </ErrorBoundary>
          </div>
        )}
        {currentStep === QTY_SELECTION_STEP && (
          <ErrorBoundary name="quantity-selection-step">
            <Suspense fallback={<ItemsToReturnSkeleton />}>
              <QuantitySelectionStep />
            </Suspense>
          </ErrorBoundary>
        )}
        {currentStep === RETURN_METHOD_STEP && (
          <ErrorBoundary name="return-method-step">
            <SelectReturnMethodStep />
          </ErrorBoundary>
        )}
        {currentStep === NUMBER_OF_BOXES_STEP && (
          <ErrorBoundary name="number-of-boxes-step">
            <DropOffBoxes />
          </ErrorBoundary>
        )}
        {currentStep === REFUND_METHOD_STEP && (
          <ErrorBoundary name="refund-method-step">
            <RefundMethodStep />
          </ErrorBoundary>
        )}
        {currentStep === LORP_RETURN_METHOD_STEP && (
          <ErrorBoundary name="lorp-return-method-step">
            <LORPReturnMethodFlow />
          </ErrorBoundary>
        )}
        {currentStep === FINALIZE_RETURN_STEP && (
          <ErrorBoundary name="finalize-return-step">
            <ReviewReturnDetailsStep />
          </ErrorBoundary>
        )}
        {currentStep === LORP_WHAT_TO_EXPECT_STEP && (
          <ErrorBoundary name="lorp-what-to-expect-step">
            <LORPWhatToExpect />
          </ErrorBoundary>
        )}
      </ErrorBoundary>
    </div>
  );
};
